
  import { defineComponent, ref, computed, watch } from "vue"

  import { useRouter } from "vue-router"

  import { ArrowNarrowDownIcon } from "@heroicons/vue/solid"

  import { StatesMap, PropertyTypesList, ExecutionTypesList, FirmTypesMap } from "@/core/constants"
  import { convertToCurrency, convertToShortCurrency, converToNumber } from "@/core/helpers/convertToCurrency"

  import Map from "./map/index.vue"

  export default defineComponent({
    name: "SignupQuizStep",

    components: {
      ArrowNarrowDownIcon,
      Map
    },

    emits: ['nextStep'],

    setup (props, { emit }) {
      const router = useRouter()

      const ExecutionTypes = new Set(ExecutionTypesList.map(item => item.Category ? item.Category : item.DisplayName))
      const PropertyTypes = new Set(PropertyTypesList.map(item => item.Category ? item.Category : item.DisplayName))

      const steps = ref<any>({
        executionType: {
          title: 'What types of commercial property loans do you offer?',
          options: Array.from(ExecutionTypes),
          collectedData: new Set(),
          isActive: true,
          isCompleted: false,
          nextStep: 'propertyType',
          type: 'selector'
        },
        propertyType: {
          title: 'What type of property do you lend on?',
          options: Array.from(PropertyTypes),
          collectedData: new Set(),
          model: 'propertyType',
          isActive: false,
          isCompleted: false,
          nextStep: 'loanAmount',
          type: 'selector'
        },
        loanAmount: {
          title: 'What are your minimum and maximum loan amounts?',
          collectedData: {
            minimumAmount: '',
            maximumAmount: ''
          },
          type: 'loanAmountRange',
          isActive: false,
          isCompleted: false,
          nextStep: 'workingArea'
        },
        workingArea: {
          title: 'What states do you lend in?',
          collectedData: [],
          type: 'map',
          isActive: false,
          isCompleted: false,
          nextStep: 'contacts'
        },
        contacts: {
          title: 'Please fill out your contact information',
          collectedData: {
            firstName: '',
            lastName: '',
            email: '',
            phone: ''
          },
          type: 'contacts',
          isActive: false,
          isCompleted: false,
          nextStep: 'submit'
        }
      })

      const visibleSteps = computed(() => {
        return Object.fromEntries(
          Object.entries(steps.value).filter((item: any) => {
            return item[1].isActive
          })
        )
      })

      const updateValue = (model, value) => {
        if (steps.value[model].collectedData.has(value)) steps.value[model].collectedData.delete(value)
        else steps.value[model].collectedData.add(value)
      }

      const processNextStep = (currentStep, nextStep) => {
        if (nextStep !== 'submit') {
          steps.value[nextStep].isActive = true
          steps.value[currentStep].isCompleted = true
        } else {
          window.localStorage.setItem(
            'originator',
            JSON.stringify(steps.value.contacts.collectedData)
          )

          router.push({ name: 'originator-settings' })
        }

        emit('nextStep', steps.value)
      }

      const validSteps = computed(() => {
        const obj = {}

        for (const key in steps.value) {
          const { type, collectedData } = steps.value[key]

          if (type == 'selector') {
            obj[key] = collectedData.size > 0
          } else if (type == 'loanAmountRange') {
            obj[key] = !!collectedData.minimumAmount && !!collectedData.maximumAmount && converToNumber(collectedData.maximumAmount) > converToNumber(collectedData.minimumAmount)
          } else if (type == 'map') {
            obj[key] = !!collectedData.length
          } else if (type == 'contacts') {
            obj[key] = !Object.values(collectedData).filter(item => !item).length
          }else {
            obj[key] = false
          }
        }

        return obj
      })

      watch(steps, (newValue, oldValue) => {
        if (steps.value.loanAmount.collectedData.minimumAmount) {
          steps.value.loanAmount.collectedData.minimumAmount = convertToCurrency(steps.value.loanAmount.collectedData.minimumAmount)
        }

        if (steps.value.loanAmount.collectedData.maximumAmount) {
          steps.value.loanAmount.collectedData.maximumAmount = convertToCurrency(steps.value.loanAmount.collectedData.maximumAmount)
        }
      }, { deep: true })

      return {
        steps,
        visibleSteps,
        updateValue,
        processNextStep,
        convertToShortCurrency,
        validSteps
      }
    }
  })
